<template>
  <div>
    <v-container>
      <h1>
        Cancellation
      </h1>

      <v-row>
        <v-col cols="12" md="8" class="text-right">
          <PolicyServicingHeader :account="account" :initalPolicy="policy" />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="8">
          <v-card class="py-8 px-8 mb-8">
            <ValidationObserver tag="span" ref="observer">
              <form @submit.prevent="validate()">

                <DynamicFormControl :control="{
                                      name: 'CancellationReason',
                                      type: 'Dropdown',
                                      value: cancellationReason,
                                      config: {
                                          label:'What is the primary reason for canceling the policy?',
                                          col: 12,
                                          small: true,
                                          options: compiledCancelationReasons,
                                          rules: 'required',
                                          itemText: 'text',
                                          itemValue: 'value',
                                      }
                                    }"
                                    @change="cancellationReason = $event.value" />

                <DynamicFormControl v-if="compiledCancelationSubReasons && compiledCancelationSubReasons.length > 0"
                                    :control="{
                                      name: 'subreason',
                                      type: 'Dropdown',
                                      value: cancellationSubReason,
                                      config: {
                                          label:'Please Select?',
                                          col: 12,
                                          small: true,
                                          options: compiledCancelationSubReasons,
                                          rules: 'required',
                                          short: true,
                                          itemText: 'text',
                                          itemValue: 'value',
                                          indented: true
                                      }
                                    }"
                                    @change="cancellationSubReason = $event.value" />

                <DynamicFormControl v-if="cancellationReason"
                                    key="CancellationDate"
                                    :control="{
                                      name: 'CancellationDate',
                                      type: 'DateInput',
                                      value: cancellationDate,
                                      indented: true,
                                      config: {
                                          label:'Cancellation Date',
                                          col: 12,
                                          rules: 'required|validDate',
                                          small: true,
                                          short: true,
                                          allowedDates: allowedDates
                                      }
                                    }"
                                    @change="cancellationDate = $event.value" />
                <Banner v-if="cancellationDate" key="CancellationDateBanner" type="warning" bannerMessage="If you cannot pick the correct cancellation effective date, please DO NOT proceed with cancelling the policy. Please contact our <span class='intercom_button'>Digital Customer Support</span> team for help selecting the correct cancellation effective date."/>

                <DynamicFormControl :control="{
                                      name: 'MailingAddressChanged',
                                      type: 'RadioButtonGroup',
                                      value: mailingAddressChanged,
                                      name: 'MailingAddressChanged',
                                      config: {
                                          options: [
                                              {value: true, text: 'Yes'},
                                              {value: false, text: 'No'}
                                          ],
                                          label: 'Has the Mailing Address changed?',
                                          small: true,
                                          short: true,
                                          disabled: loading,
                                          mandatory: true,
                                          required: true,
                                          rules: 'required',
                                          description: mailingAddressText,
                                          footnote: mailingAddressText,
                                          footnoteClasses: 'footnote-bold'
                                      }
                                  }"
                                    @change="mailingAddressChanged = $event.value" />

                <DynamicFormControl v-if="mailingAddressChanged"
                                    :control="{
                                      name: 'MailingAddress',
                                      type: 'Address',
                                      value: mailingAddress,
                                      indented: true,
                                      config: {
                                          label: 'Mailing Address',
                                          col: 12,
                                          rules: 'required',
                                          short: true,
                                          addressType: 'MAILING'
                                      }
                                    }"
                                    @change="mailingAddress = $event.value" />
                <div class="footnote-bold">
                  <span>Please note that we will be mailing a copy of the cancellation document to the insured and (if applicable) any mortgagees listed on the policy.</span>
                </div>
                <Banner v-if="shouldShowPending" type="warning"
                    bannerMessage="The inspection is currently in a pending state. We will automatically request cancellation of the inspection upon completion of the cancellation endorsement."/>
                <Banner v-if="shouldShowComplete" type="warning"
                    bannerMessage="The inspection has been completed. Returning the inspection fee will result in Amwins absorbing the cost of the inspection."/>

                <div class="fees-section-title">
                  <span>Fees:</span>
                </div>
                <Banner type="info" bannerMessage="Note:  Service and Inspection Fees are Fully Earned. If any questions, please reach out to <span class='intercom_button'>Digital Customer Support</span> prior to submitting cancellation."/>

                <div v-if="canOverrideFees" class="fees-wrapper">
                  <Checkbox :value="overrideFees" label="Override Fees" @change="overrideFees = $event"/>
                  <div v-if="overrideFees">
                  <Checkbox :value="overrideAmwinsServiceFee" label="Return Amwins Service fee" @change="overrideAmwinsServiceFee = $event"/>
                  <Checkbox v-if="hasInspectionFee" :value="overrideInspectionFee" label="Return Inspection fee" @change="overrideInspectionFee = $event"/>
                  <Checkbox v-if="hasMarketPolicyFee" :value="overrideMarketPolicyFee" label="Return Market Policy fee" @change="overrideMarketPolicyFee = $event"/>
                  </div>
                </div>

                <DynamicFormControl :control="{
                                      name: 'ElectronicSignature',
                                      type: 'Attestation',
                                      value: attestation,
                                      config: {
                                          label:'',
                                          col: 12,
                                          useGlobalAgentAttestation: true,
                                          rules: 'required',
                                      }
                                    }"
                                    @change="attestation = $event.value" />

                <div class="text-right">
                  <v-btn color="white" large :disabled="loading" @click="returnToAccount" class="ml-4">
                    <Loading v-if="loading" :size="24" class="mr-2" />
                    Cancel
                  </v-btn>
                  <v-btn color="secondary" large type="submit" :disabled="loading" class="ml-4">
                    <Loading v-if="loading" :size="24" class="mr-2" />
                    Submit
                  </v-btn>
                </div>
              </form>
            </ValidationObserver>
          </v-card>
        </v-col>
      </v-row>
      <Dialog
        v-model="showRenewalModal"
        title="Cancel Policy and Upcoming Term"
        @close-dialog="showRenewalModal = false"
        width="500"
        :cardTitleStyle="'padding: 24px 24px 0 24px; border-bottom: 1px solid #E1E6EF;'"
        titleFontWeight="600"
        titleFontSize="16px"
>
        <template #card-text>
          <div class="dialog-content">
            Submitting the cancellation request for the expiring policy will also cancel the upcoming term.
          </div>
        </template>

        <template #card-actions>
          <v-spacer />
            <v-btn
              class="dialog-cancel-btn"
              @click="showRenewalModal = false"
              text
    >
                  Cancel
            </v-btn>
            <v-btn
              class="dialog-submit-btn"
              @click="confirmCancellation"
    >
                 Submit Cancellation
            </v-btn>
        </template>
      </Dialog>
    </v-container>
  </div>
</template>

<script>
  import DynamicFormControl from '@/components/form/DynamicFormControl.vue';
  import Banner from '@/components/ui/Banner.vue';
  import PolicyServicingHeader from '@/components/views/Account/PolicyServicingHeader.vue';
  import { AccountService } from '@/api/AccountService';
  import { PolicyService } from '@/api/PolicyService';
  import dateformat from 'dateformat';
  import { formatDateForDisplay, isV2SalesChannel } from '@/scripts/helper';
  import Checkbox from '@/components/form/Checkbox.vue';
  import { InspectionsService } from '@/api/InspectionsService';
  import { getUtcDate } from '@/scripts/date-helper';
  import Dialog from '@/components/shared/Dialog.vue';

  export default {
    name: 'Cancellation',
    components: {
      DynamicFormControl,
      PolicyServicingHeader,
      Banner,
      Checkbox,
      Dialog
    },
    data () {
      return {
        shouldShowPending: false,
        shouldShowComplete: false,
        account: null,
        accountId: '',
        loading: false,
        cancellationReason: '',
        cancellationSubReason: '',
        cancellationDate: '',
        mailingAddress: null,
        mailingAddressChanged: null,
        attestation: null,
        cancellationReasons: null,
        policy: null,
        policyId: '',
        hasInspectionFee: false,
        overrideFees: false,
        overrideAmwinsServiceFee: false,
        overrideInspectionFee: false,
        hasMarketPolicyFee: false,
        overrideMarketPolicyFee: false,
        showRenewalModal: false,
        allowedDates: (val) => {
          const termBeginDate = this.formatDate(this.policy.Policy.TermBegin.split('T')[0]);
          const today = getUtcDate(new Date());
          const formattedVal = getUtcDate(val);
          const formattedValWithoutTime = this.getDateWithoutTime(new Date(formattedVal));
          const termBeginDateWithoutTime = this.getDateWithoutTime(new Date(termBeginDate));
          const todayWithoutTime = this.getDateWithoutTime(today);

          // Users with access to Cancellation_Backdate restricted feature can backdate up to the TermBegin date (regardless of Cancellation reason)
          if (this.canBackdateCancellations && new Date(termBeginDate) <= today) {
            return new Date(termBeginDate) <= new Date(formattedVal) && new Date(formattedVal) < today;
          }

          // if the policy is in the future, cancellation date must be TermBegin date (regardless of Cancellation reason)
          if (new Date(termBeginDate) >= today) {
            return formattedValWithoutTime.toISOString() === termBeginDateWithoutTime.toISOString();
          }

          // Cancellation date must be policy effective date
          if (this.cancellationReason.Value == 'No longer need the coverage' && this.cancellationSubReason.Value == 'Void prior to effective date') {
            return formattedValWithoutTime.toISOString() === termBeginDateWithoutTime.toISOString();
          }

          // Cancellation date can be backdated to TermBegin date if it's within 30 days of the current date and cannot be a future date
          if (this.cancellationReason.Value == 'No longer need the coverage' && this.cancellationSubReason.Value == 'Policy is not acceptable by Mortgagee' && new Date(termBeginDate) >= this.addDays(today, -31)) {
            // Allow 30 days to back date
            let maxBackDate = new Date(Math.max(termBeginDateWithoutTime, this.addDays(today, -31))); // -31 to get 30 days inclusive of today
            return formattedValWithoutTime >= maxBackDate && formattedValWithoutTime <= todayWithoutTime;
          }

          // Cancellation date can be up to 30 days backdated and cannot be a future date
          if (this.cancellationReason.Value == 'Property Sold') {
            // allowed to back date up to 30 days or the beginning of the policy term, whichever is later
            let maxBackDate = new Date(Math.max(termBeginDateWithoutTime, this.addDays(today, -31))); // -31 to get 30 days inclusive of today
            return formattedValWithoutTime >= maxBackDate && formattedValWithoutTime <= todayWithoutTime;
          }

          // Cancellation date can be backdated to TermBegin date if it's within 10 days of the current date and cannot be a future date
          if (this.cancellationReason.Value == 'Closing fell through' && new Date(termBeginDate) >= this.addDays(today, -11)) { // -11 to get 10 days inclusive of today
            // allowed to back date up to 10 days or the beginning of the policy term, whichever is later
            let maxBackDate = new Date(Math.max(termBeginDateWithoutTime, this.addDays(today, -11))); // -11 to get 10 days inclusive of today
            return formattedValWithoutTime >= maxBackDate && formattedValWithoutTime <= todayWithoutTime;
          }

          // if no special handling - Cancellation Date must be today
          return formattedValWithoutTime.toISOString() === todayWithoutTime.toISOString();
        }
      };
    },
    watch: {
      overrideFees (val) {
        if (!val) {
          this.overrideAmwinsServiceFee = false;
          this.overrideInspectionFee = false;
          this.overrideMarketPolicyFee = false;
        }
      },
      cancellationReason (val) {
        const termBeginDate = this.formatDate(this.policy.Policy.TermBegin.split('T')[0]);
        const today = new Date();

        if (val.Value == 'Non-Payment' || new Date(termBeginDate) >= today) {
          this.cancellationDate = dateformat(termBeginDate, 'mm/dd/yyyy');
        }

        if ((val.Value == 'Moved policy to another Insurance Company' || val.Value == 'Mortgage Paid Off' || val.Value == 'Cancel to Rewrite') && new Date(termBeginDate) < today) {
          // Date must be today
          const dformat = dateformat(this.getDateWithoutTime(today), 'mm/dd/yyyy');
          this.cancellationDate = dformat;
        }

        if (val.Value == 'Closing fell through') {
          if (new Date(termBeginDate) >= this.addDays(today, -11)) {
            // Set date to Term Begin Date. User may change it.
            this.cancellationDate = dateformat(termBeginDate, 'mm/dd/yyyy');
          } else {
            // Date must be today
            const dformat = dateformat(this.getDateWithoutTime(today), 'mm/dd/yyyy');
            this.cancellationDate = dformat;
          }
        }

        this.newFloodProvider = '';
      },
      cancellationSubReason (val) {
        const termBeginDate = this.formatDate(this.policy.Policy.TermBegin.split('T')[0]);
        const today = new Date();
        if (val.Value == 'Void prior to policy effective date') {
          // Date must be Term Begin date
            this.cancellationDate = termBeginDate;
        }

        if (val.Value == 'Policy is not acceptable by Mortgagee') {
          if (new Date(termBeginDate) >= this.addDays(today, -31)) {
            // Set date to Term Begin Date. User may change it.
              this.cancellationDate = termBeginDate;
          } else {
            // Date must be today
            const dformat = dateformat(this.getDateWithoutTime(today), 'mm/dd/yyyy');
            this.cancellationDate = dformat;
          }
        }
      },
      mailingAddressChanged (newValue) {
        if (!newValue) {
          // Reset mailing address when mailingAddressChanged is false
          this.mailingAddress = null;
        }
      }
    },
    async mounted () {
      this.accountId = this.$route.query.accountId;
      this.policyId = this.$route.query.policyId;
      // Lets load in our current policy
      PolicyService.getV2({ submissionId: this.accountId, policyId: this.policyId })
        .then((response) => {
          if (response) {
            this.policy = response;
            if (this.policy.Policy?.AnnualizedPricing?.Fees.find(x => x.Classification.Id === 'fb728a3d-46e3-4def-955e-27eeba3d5ed7')) {
              this.hasInspectionFee = true;
            }
            if (this.policy.Policy?.AnnualizedPricing?.Fees.find(x => x.Classification.Id === '02c18602-6dec-4e4e-8074-735865d1382d')) {
              this.hasMarketPolicyFee = true;
            }

            PolicyService.getCancellationReasons({ id: this.policy.Policy.ProductType.Id, value: this.policy.Policy.ProductType.Value.toLowerCase() }).then((data) => {
              this.cancellationReasons = data.CancellationReasons;
            });

            this.setInspectionBannerDisplay();
          }
        })
        .catch(() => {
          this.$store.dispatch('toastError', { Message: 'Failed to retrieve policy data' });
        });

      if (this.accountId) {
        this.loading = true;
        await AccountService.get({ id: this.accountId })
          .then((data) => {
            this.account = data;
            if (this.$store.getters.userHasRole('agent') && isV2SalesChannel(this.account.SalesChannel)) {
              // per user story 597109, don't allow agents for flood or vave do OG cancellations
              this.$router.push({ path: '/account', query: { id: this.accountId } });
            }
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
    computed: {
      compiledCancelationReasons () {
        let reasons = [];
        for (let i = 0; i < this.cancellationReasons?.length; i++) {
          reasons.push({ text: this.cancellationReasons[i].CancellationReason.Value, value: this.cancellationReasons[i].CancellationReason });
        }
        return reasons;
      },
      compiledCancelationSubReasons () {
        let subreasons = [];
        var reason = this.cancellationReasons?.find(x => x.CancellationReason == this.cancellationReason);
        if (reason?.CancellationSubReasons.length > 0) {
          subreasons = reason.CancellationSubReasons.map(x => { return { text: x.Value, value: x }; });

          const termBeginDate = new Date(this.formatDateForDisplay(this.policy.Policy.TermBegin, true));
          const today = this.getDateWithoutTime(new Date());
          if (termBeginDate < today) {
            subreasons = subreasons.filter(x => x.text != 'Void prior to policy effective date');
          }
        } else if (reason?.MissingRequiredDocument.length > 0) {
          subreasons = reason.MissingRequiredDocument.map(x => { return { text: x, value: x }; });
        }
        return subreasons;
      },
      mailingAddressText () {
          return 'If you require the Cancellation Notice to be sent to an alternate address, please enter a new mailing address.';
      },
      canBackdateCancellations () {
        return this.$store.getters.userHasRestrictedFeature('Cancellation_Backdate');
      },
      canOverrideFees () {
        return this.$store.getters.userHasRestrictedFeature('cancellation_fee_override');
      },
      hasRenewal () {
        const hasRenewalOffer = this.account?.AccountStatus === "RenewalOffered";
        const hasFuturePolicyTerms = this.account?.TermList?.some(term =>
          term.id !== this.policy?.Policy?.id &&
          term.PolicyTermStatus === 'Issued' &&
          new Date(term.TermBegin) > new Date(this.policy?.Policy?.TermBegin)
        );

        return hasRenewalOffer || hasFuturePolicyTerms;
      }
    },
    methods: {
      formatDateForDisplay,
      returnToAccount () {
        this.$router.push({ path: '/account', query: { id: this.account.id } });
      },
      addDays (date, days) {
        var result = new Date(date);
        result.setDate(result.getDate() + days);
        return result;
      },
      getDateWithoutTime (date) {
        var dateString = date.toDateString();
        return new Date(dateString);
      },
      formatDate (date) {
            if (!date) return null;
            const [year, month, day] = date.split('-');
            return `${month}/${day}/${year}`;
      },
      validate () {
        this.loading = true;
        this.$refs.observer.validate()
          .then(valid => {
            if (valid) {
              if (this.hasRenewal) {
                this.showRenewalModal = true;
              } else {
                  this.submitV2Cancellation();
                }
      }
            this.loading = false;
          });
      },
      submitV2Cancellation () {
        let cancellationRequest = {};
        cancellationRequest.AccountId = this.accountId;
        cancellationRequest.CancellationReason = this.cancellationReason;
        if (this.cancellationReason.Value == 'Missing Required Documents') {
          cancellationRequest.MissingRequiredDocument = this.cancellationSubReason;
        } else {
          cancellationRequest.CancellationSubReason = this.cancellationSubReason;
        }
        cancellationRequest.CancellationEffectiveDate = this.cancellationDate;
        cancellationRequest.MailingAddress = this.mailingAddress;
        cancellationRequest.OverrideFees = this.overrideFees;
        cancellationRequest.OverrideAmwinsServiceFee = this.overrideAmwinsServiceFee;
        cancellationRequest.OverrideInspectionFee = this.overrideInspectionFee;
        cancellationRequest.OverrideMarketPolicyFee = this.overrideMarketPolicyFee;
        cancellationRequest.PolicyId = this.policyId;

        PolicyService.cancelRequestV2({ body: cancellationRequest })
          .then(() => {
            this.$router.push({ path: '/account', query: { id: this.account.id } }).then(() => {
              this.$store.dispatch('toastSuccess', { Message: 'Successfully submitted cancellation request' });
            });
          })
          .catch(() => {
            this.$store.dispatch('toastError', { Message: 'Failed to process cancellation request' });
          });
      },
      setInspectionBannerDisplay () {
        if (!this.hasInspectionFee || !this.canOverrideFees) { return; }

        InspectionsService.inspectionStatus({
          policyId: this.policy.id,
          policyNumber: this.policy.Policy.PolicyNumber
          }, {
              handleError: false
          })
          .then((data) => {
            if (data) {
              const canCancel = data?.canCancelInspection ?? true;
                this.shouldShowPending = canCancel;
                this.shouldShowComplete = !canCancel;
            }
          })
          .catch(error => {
                if (error?.response?.status === 500) {
                this.shouldShowPending = true;
                  this.shouldShowComplete = false;
              } else {
                    this.$store.dispatch('toastError', { Message: 'Failed to retrieve inspection data' });
              }
          });
      },
      confirmCancellation () {
        this.showRenewalModal = false;
        this.submitV2Cancellation();
      }
    }
  };
</script>

<style scoped lang="scss">
.footnote-bold {
  font-weight: bold;
  margin-bottom: 0;
  margin-top: 10px;
}
.fees-section-title{
  font-weight: bold;
  margin-top:10px;
  padding-top: 10px;
  border-top: 1px solid #DDDDDD;
}
.fees-wrapper {
  margin-bottom:10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #DDDDDD;
}
:deep(.intercom_button) {
  cursor: pointer;
  text-decoration: underline;
}
.dialog-content {
  padding: 16px 24px 24px;
  font-size: 15px;
  color: #4A4A4A;
  line-height: 1.6;
}
.dialog-cancel-btn {
  border: 1.5px solid #0D2940;
  color: #0D2940;
  background-color: white;
  font-weight: 600;
  font-size: 14px;
  padding: 8px 16px;
  border-radius: 6px;
  text-transform: none;
  min-width: 140px;
  box-shadow: none !important;
}
.dialog-submit-btn {
  background-color: #0D2940 !important;
  color: white !important;
  font-weight: 600;
  font-size: 14px;
  padding: 8px 16px;
  border-radius: 6px;
  text-transform: none;
  min-width: 140px;
  box-shadow: none !important;
}
</style>
